import { useState } from "react";
import moment from "moment";
import { User } from "../../types/User";
import useUserService from "../../services/UserService";
import NegativeButton from "../NegativeButton";
import PositiveButton from "../PositiveButton";
import NeutralButton from "../NeutralButton";

interface GiftedSubscriptionPanelProps {
  user: User;
}

/** Value shape the datetime-local input expects, in the admin's own timezone. */
const toInputValue = (date: Date) => moment(date).format("YYYY-MM-DDTHH:mm");

const QUICK_PICKS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

/**
 * Grants and revokes a free pro subscription. A gift can either run indefinitely or be a manual trial
 * that lapses at a chosen moment; the API stops honouring an expired gift on its own, so an expired
 * user comes back with no SKU but still reports the expiry we ran past.
 */
export default function GiftedSubscriptionPanel({ user }: GiftedSubscriptionPanelProps) {
  const { enableGiftedSubscription, disableGiftedSubscription } = useUserService();

  const [sku, setSku] = useState<string | null>(user.giftedSubscriptionSKU);
  const [expiry, setExpiry] = useState<string | null>(user.giftedSubscriptionExpiry);
  const [expiresAtInput, setExpiresAtInput] = useState<string>(
    user.giftedSubscriptionExpiry ? toInputValue(new Date(user.giftedSubscriptionExpiry)) : ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEnabled = !!sku;
  const hasLapsed = !sku && !!expiry;

  const setQuickPick = (days: number) =>
    setExpiresAtInput(toInputValue(moment().add(days, "days").toDate()));

  const grant = async (expiresAt: Date | null) => {
    setIsSaving(true);
    setError(null);

    try {
      await enableGiftedSubscription(user.id, expiresAt);
      setSku("beamxr_pro");
      setExpiry(expiresAt ? expiresAt.toISOString() : null);
      setExpiresAtInput(expiresAt ? toInputValue(expiresAt) : "");
    } catch {
      setError("Couldn't update the gifted subscription. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const grantUntilChosenDate = async () => {
    const expiresAt = new Date(expiresAtInput);

    if (isNaN(expiresAt.getTime())) {
      setError("Pick a date and time for the trial to end.");
      return;
    }

    if (expiresAt <= new Date()) {
      setError("The end date has to be in the future.");
      return;
    }

    await grant(expiresAt);
  };

  const revoke = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await disableGiftedSubscription(user.id);
      setSku(null);
      setExpiry(null);
      setExpiresAtInput("");
    } catch {
      setError("Couldn't update the gifted subscription. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const status = () => {
    if (isEnabled && expiry) {
      return `Trial - user has a free subscription until ${moment(expiry).format("D MMM YYYY, HH:mm")} (${moment(expiry).fromNow()})`;
    }

    if (isEnabled) {
      return "Enabled - User has a free subscription from the BeamXR Team, with no end date";
    }

    if (hasLapsed) {
      return `Expired - the user's trial ran out ${moment(expiry).fromNow()}, on ${moment(expiry).format("D MMM YYYY, HH:mm")}`;
    }

    return "Disabled - user doesn't have a free subscription";
  };

  return (
    <div className="flex flex-col gap-2">
      <p>{status()}</p>

      <label htmlFor="giftedSubscriptionExpiry" className="block leading-6">
        {isEnabled ? "Change the end date:" : "Give a trial until:"}
      </label>
      <input
        id="giftedSubscriptionExpiry"
        type="datetime-local"
        disabled={isSaving}
        value={expiresAtInput}
        min={toInputValue(new Date())}
        onChange={(e) => setExpiresAtInput(e.target.value)}
        className="w-full px-3 py-2 border rounded text-black"
      />

      <div className="flex flex-row flex-wrap gap-2">
        {QUICK_PICKS.map((pick) => (
          <NeutralButton key={pick.days} disabled={isSaving} onClick={() => setQuickPick(pick.days)}>
            {pick.label}
          </NeutralButton>
        ))}
      </div>

      <PositiveButton disabled={isSaving || !expiresAtInput} onClick={grantUntilChosenDate}>
        {isEnabled ? "Update trial end date" : "Give trial until this date"}
      </PositiveButton>

      {isEnabled ? (
        <NegativeButton disabled={isSaving} onClick={revoke}>
          Disable
        </NegativeButton>
      ) : (
        <NeutralButton disabled={isSaving} onClick={() => grant(null)}>
          Enable with no end date
        </NeutralButton>
      )}

      {isEnabled && !expiry && (
        <p className="text-sm text-white/70">
          Pick a date above to turn this into a trial that ends on its own.
        </p>
      )}

      {error && <div className="text-white bg-red-800 py-1 px-2 rounded-xl">{error}</div>}
    </div>
  );
}
